import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, Checkbox, Space } from 'antd';
import {
  useSetBatchForwardEnable,
  useBatchForwardConvIds,
  useResetBatchForwardConvIds,
  useSetBatchForwardConvIds,
  useConvCanNotForward,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { useMemoizedFn } from 'ahooks';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useConversationListFilter, useIsConversationDescMode } from 'hooks/business/conversation';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { defaultBatchTransferMaxLimit } from 'services/config/app';

import { useTranslate } from '@tolgee/react';
import { useGetConversationById } from 'hooks/business/lruConversation';

const Container = styled(Space)`
  min-height: 64px;
  padding: 8px;
  box-shadow: 0px -2px 8px 0px rgba(167, 178, 204, 0.1),
    inset 0px 1px 0px 0px rgba(240, 242, 245, 1);
  justify-content: space-between;
`;

export interface BatchForwardProps {
  onForward?: (batchForwardIds: string[]) => void;
  cacheConvIds: string[];
}

const BatchForward = (props: BatchForwardProps) => {
  const { onForward, cacheConvIds } = props;
  const setBatchForwardEnable = useSetBatchForwardEnable();
  const batchForwardConvIds = useBatchForwardConvIds();
  const conversationList = useConversationListFilter();
  const isConversationDescMode = useIsConversationDescMode();
  const conversationIdList = isConversationDescMode
    ? cacheConvIds
    : conversationList.map(({ conversationId }) => conversationId);

  const resetBatchForwardConvIds = useResetBatchForwardConvIds();
  const setBatchForwardConvIds = useSetBatchForwardConvIds();
  const convCanNotForward = useConvCanNotForward();
  const getConversationById = useGetConversationById();

  const { t } = useTranslate();

  const { batchTransferMaxLimit = defaultBatchTransferMaxLimit } = useFlagConfigValue();

  const enableForwardIds = useMemo(() => {
    return conversationIdList.filter((id) => {
      const conv = getConversationById(id);
      return conv && !(!conv.controlStatus || convCanNotForward(conv.conversationId));
    });
  }, [convCanNotForward, conversationIdList, getConversationById]);
  const forwardBtnDisabled = useMemo(
    () => batchForwardConvIds.length === 0,
    [batchForwardConvIds.length],
  );

  const indeterminate = useMemo(() => {
    const batchLen = batchForwardConvIds.length;
    const idsLen = enableForwardIds.length;

    return !!(batchLen && batchLen < idsLen);
  }, [batchForwardConvIds.length, enableForwardIds.length]);

  const handleCheck = useMemoizedFn((e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    if (checked) {
      setBatchForwardConvIds(enableForwardIds);
    } else {
      resetBatchForwardConvIds();
    }
  });

  const handleCancel = useMemoizedFn(() => {
    setBatchForwardEnable(false);
    resetBatchForwardConvIds();
  });

  return (
    <Container wrap>
      <Checkbox
        indeterminate={indeterminate}
        checked={enableForwardIds.length === batchForwardConvIds.length}
        onChange={handleCheck}
      >
        {t('scene.chatWorkspace.forwardDialog.batch.selectCurrentPage', {
          selected: batchForwardConvIds.length || 0,
          limit: batchTransferMaxLimit,
        })}
      </Checkbox>
      <Space>
        <Button disabled={forwardBtnDisabled} onClick={() => onForward?.(batchForwardConvIds)}>
          {t('scene.chatWorkspace.conversationList.action.transfer')}
        </Button>
        <Button onClick={handleCancel}>{t('common.action.cancel')}</Button>
      </Space>
    </Container>
  );
};

export default BatchForward;
